import { Flex, Modal } from 'antd';
import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Icon } from '../Icon';

import { ActivityItem, MaxHeightUl, SyncedWarning, Title } from './QuizDeleteActivityModal.styles';

import { useQuizDeletionModal } from '@/hooks';
import { useActivityByQuiz, useMutateMyQuizzes } from '@/store';

type Props = {
  isQuizDetail?: boolean;
};

const MODAL_WIDTH = 600;

export const QuizDeleteActivityModal: React.FC<Props> = ({ isQuizDetail }) => {
  const { quizId, setQuizId } = useQuizDeletionModal();
  const { activities } = useActivityByQuiz(quizId);
  const { removeQuiz, isWorking: isWorkingMutations } = useMutateMyQuizzes();
  const totalSyncedActivities = useMemo(
    () =>
      activities?.reduce((acc, activity) => acc + (activity.syncedActivities?.length ?? 0), 0) ?? 0,
    [activities]
  );
  const { t } = useTranslation(['CommonQuizFormTranslations', 'DefaultTranslations']);
  const navigateTo = useNavigate();

  const handleNavigateToActivity = (courseId: string | undefined) => {
    if (!courseId) return;
    navigateTo(`/content/courses/${courseId}?activeTab=CONTENT&contentTab=SECTIONS`);
  };

  const handleRemoveQuiz = () => {
    if (!quizId) return;
    removeQuiz(quizId, {
      onSuccess: () => {
        setQuizId(undefined);
        if (isQuizDetail) {
          navigateTo(-1);
        }
      }
    });
  };

  useEffect(() => {
    return () => {
      setQuizId(undefined);
    };
  }, []);

  return (
    <Modal
      open={!!quizId}
      centered
      okText={t('CommonQuizFormTranslations:DELETE_QUIZ_MODAL_BUTTON', {
        count: activities?.length ?? 0
      })}
      onOk={handleRemoveQuiz}
      cancelText={t('DefaultTranslations:CANCEL')}
      onCancel={() => setQuizId(undefined)}
      okButtonProps={{ loading: isWorkingMutations }}
      width={MODAL_WIDTH}
      wrapProps={{ onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation() }}>
      <Title>
        <Icon i="WarningAltFilled" color="orange6" size={20} />
        {t('CommonQuizFormTranslations:DELETE_QUIZ_MODAL_TITLE')}
      </Title>

      <Flex vertical gap={24}>
        <span>
          <Trans
            i18nKey="CommonQuizFormTranslations:DELETE_QUIZ_MODAL_DESCRIPTION"
            components={{ br: <br /> }}
          />
        </span>

        <Flex vertical>
          <span style={{ fontWeight: 500 }}>{t('CommonQuizFormTranslations:TWO_FORMS')}</span>

          <MaxHeightUl>
            <ActivityItem>
              <span>{t('CommonQuizFormTranslations:FORM_ONE')}</span>
            </ActivityItem>

            <ActivityItem>
              <span>{t('CommonQuizFormTranslations:FORM_TWO')}</span>
            </ActivityItem>
          </MaxHeightUl>
        </Flex>

        <Flex vertical style={{ paddingBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>
            {t('CommonQuizFormTranslations:DELETE_QUIZ_MODAL_ACTIVITIES_TITLE')}
          </span>

          <MaxHeightUl>
            {activities?.map(activity => (
              <ActivityItem key={activity._id}>
                <span onClick={() => handleNavigateToActivity(activity?.course?._id)}>
                  {activity?.course?.name} {'>'} {activity?.section?.name} {'>'} {activity.name}
                </span>
              </ActivityItem>
            ))}
          </MaxHeightUl>

          {totalSyncedActivities > 0 && (
            <SyncedWarning>
              <Icon i="WarningAltFilled" color="orange6" size={14} />
              <Trans
                i18nKey="CommonQuizFormTranslations:SYNCED_ACTIVITIES_WARNING"
                count={totalSyncedActivities}
                components={{ strong: <strong /> }}
              />
            </SyncedWarning>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
